/**
 * Undo, on a board two people are drawing at once.
 *
 * Each batch is inverted against the board as it stood *before* it — the
 * opposite of what it did, in the opposite order — and the inverse is sent
 * through the ordinary write path. Nothing here reaches behind the server or
 * keeps a private copy of the document.
 *
 * What that buys, and what it does not: undo takes back *your* last action,
 * not the other person's, because the stack only holds what you did. If they
 * have since moved the same figure, undoing puts it back where you had it —
 * last writer wins, exactly as any other edit would. A shared board cannot
 * have an undo that is both simple and impossible to surprise, and the
 * surprising case here is rare and visible: the figure moves, in front of
 * both of you.
 */

import { applyLocally, type Operation } from "./operations";
import type { Household, MapLink, MapNode, MapState } from "./types";

const findNode = (state: MapState, id: unknown): MapNode | undefined =>
    state.nodes.find((node) => node.id === id);

const findLink = (state: MapState, id: unknown): MapLink | undefined =>
    state.links.find((link) => link.id === id);

const findHousehold = (state: MapState, id: unknown): Household | undefined =>
    state.households.find((household) => household.id === id);

/** Every link that would go with this node, so undo can put them back too. */
const linksTouching = (state: MapState, id: string): MapLink[] => {
    const gone = new Set<string>([id]);
    const removed: MapLink[] = [];
    let changed = true;
    while (changed) {
        changed = false;
        for (const link of state.links) {
            if (removed.includes(link)) continue;
            if (gone.has(link.from) || gone.has(link.to)) {
                removed.push(link);
                gone.add(link.id);
                changed = true;
            }
        }
    }
    return removed;
};

/** The one operation that undoes another, given the board it was applied to. */
const invertOne = (state: MapState, operation: Operation): Operation[] => {
    const { op, payload } = operation;
    const id = payload.id as string;

    switch (op) {
        case "upsert_node": {
            const before = findNode(state, id);
            return [
                before
                    ? { op: "upsert_node", payload: { ...before } }
                    : { op: "remove_node", payload: { id } },
            ];
        }
        case "move_node": {
            const before = findNode(state, id);
            return before
                ? [{ op: "move_node", payload: { id, x: before.x, y: before.y } }]
                : [];
        }
        case "remove_node": {
            const before = findNode(state, id);
            if (!before) return [];
            // The node first, then the links that fell with it — a link cannot
            // be put back before the person it points at.
            return [
                { op: "upsert_node", payload: { ...before } },
                ...linksTouching(state, id).map((link) => ({
                    op: "upsert_link" as const,
                    payload: { ...link },
                })),
            ];
        }
        case "upsert_link": {
            const before = findLink(state, id);
            return [
                before
                    ? { op: "upsert_link", payload: { ...before } }
                    : { op: "remove_link", payload: { id } },
            ];
        }
        case "remove_link": {
            const before = findLink(state, id);
            if (!before) return [];
            const children = state.links.filter((link) => link.from === id);
            return [
                { op: "upsert_link", payload: { ...before } },
                ...children.map((link) => ({
                    op: "upsert_link" as const,
                    payload: { ...link },
                })),
            ];
        }
        case "upsert_household": {
            const before = findHousehold(state, id);
            return [
                before
                    ? { op: "upsert_household", payload: { ...before } }
                    : { op: "remove_household", payload: { id } },
            ];
        }
        case "remove_household": {
            const before = findHousehold(state, id);
            return before
                ? [{ op: "upsert_household", payload: { ...before } }]
                : [];
        }
        case "set_meta":
            return [{ op: "set_meta", payload: { ...state.meta } }];
        default:
            return [];
    }
};

/**
 * The batch that undoes this one.
 *
 * Walked forward to know what each operation met, and returned in reverse:
 * undoing "add a person, then move them" is "put them back, then take them
 * away", and doing it the other way round would move a figure that is no
 * longer there.
 */
export const invert = (state: MapState, operations: Operation[]): Operation[] => {
    let working = state;
    const inverses: Operation[][] = [];

    for (const operation of operations) {
        inverses.push(invertOne(working, operation));
        working = applyLocally(working, [operation]);
    }

    return inverses.reverse().flat();
};
